import { WE_CHAT_OFFICIAL_ACCOUNT_APP_ID, WEB_DOMAIN, APP_NAME } from "utils/Config";
import { matchPath } from 'react-router-dom';
import gcoord from 'gcoord'

import {
    BASIC_WEBAPP_ROUTES,
    GIFT_CARD_ROUTE,
    ABOUT_NAV_ROUTE,
    USER_NAV_ROUTE,
    OFFER_ROUTE,
} from "utils/RouteUtil";

import { mobileDetectIsInMainNav, getCamelCase } from 'component/common/navigator/NavigatorUtils'

export function openMiniProgramMap({ latitude, longitude }) {
    if (!window.wx) {
        return
    }
    let mapLatitude = latitude
    let mapLongitude = longitude
    const mainlandChina = Intl.DateTimeFormat().resolvedOptions()?.timeZone === "Asia/Shanghai";
    if (mainlandChina && gcoord) {
        const result = gcoord.transform(
            [longitude, latitude],
            gcoord.WGS84,
            gcoord.GCJ02
        );
        mapLongitude = result[0]
        mapLatitude = result[1]
    }
    window.wx.openLocation({
        latitude: mapLatitude,
        longitude: mapLongitude,
        scale: 18
    })
}

export function requestMiniProgramLocation({ onSuccess, onFailed, onCancel }) {
    if (!window.wx) {
        return
    }
    window.wx.ready(function () {
        window.wx.getLocation({
            type: 'wgs84',
            success: function (res) {
                console.log("getLocation success:", res)
                onSuccess && onSuccess(res)
            },
            fail: function (err) {
                console.log("getLocation fail:", err)
                onFailed && onFailed(err.errMsg)
            },
            cancel: function (res) {
                console.log("getLocation cancel:", res)
                onCancel && onCancel()
            }
        });
    })
}

export function requestMiniProgramScan({ onSuccess, onFailed, onCancel }) {
    if (!window.wx) {
        return
    }
    window.wx.scanQRCode({
        needResult: 1,
        scanType: ["qrCode", "barCode"],
        success: function (res) {
            const result = res.resultStr;
            console.log("scanQRCode success:", result)
            onSuccess && onSuccess(result)
        },
        fail: function (err) {
            console.log("scanQRCode error:", err)
            onFailed && onFailed(err.errMsg)
        },
        cancel: function (res) {
            console.warn('scanQRCode cancel:', res);
            onCancel && onCancel()
        }
    });
}

export function requestMiniProgramChooseImage({ onSuccess, onFailed, onCancel }) {
    if (!window.wx) {
        return
    }
    window.wx.chooseImage({
        count: 1,
        sizeType: ['original', 'compressed'],
        sourceType: ['album', 'camera'],
        success: function (res) {
            const localIds = res.localIds;
            console.log("chooseImage success:", localIds)
            onSuccess && onSuccess(localIds)
        },
        fail: function (err) {
            console.log("chooseImage fail:", err)
            onFailed && onFailed(err.errMsg)
        },
        cancel: function (res) {
            console.warn('chooseImage cancel:', res);
            onCancel && onCancel()
        }
    });
}

export function inMiniProgram() {
    if (window.__wxjs_environment === "miniprogram") {
        return true
    }
    const ua = window.navigator.userAgent.toLowerCase()
    const isWXWork = ua.match(/wxwork/i) && ua.match(/wxwork/i)[0] === 'wxwork'
    const isWeixinMiniProgram = !isWXWork && ua.match(/miniprogram/i) && ua.match(/miniprogram/i)[0] === 'miniprogram'
    if (isWeixinMiniProgram) {
        return true
    }
    return false
}

export function setWeChatConfig(timestamp, nonceStr, signature) {
    if (!window.wx) {
        return
    }
    console.log("setWeChatConfig:", timestamp, nonceStr, signature)
    window.wx.config({
        debug: false,
        appId: WE_CHAT_OFFICIAL_ACCOUNT_APP_ID,
        timestamp: timestamp,
        nonceStr: nonceStr,
        signature: signature,
        jsApiList: [
            "scanQRCode",
            "getLocation",
            "chooseImage",
            "openLocation",
        ]
    });
}

/**
 * Navigate to a specified page or link
 * 
 * @param {Object} options - Navigation options
 * @param {string|null} options.pathname - Relative path name, defaults to null
 * @param {string|null} options.fullLink - Full link address, defaults to null
 * @param {boolean} options.skipWeChatNewStack - Whether it skip to create a new stack in we chat, defaults to false
 * @param {Object} options.history - Router history object for page navigation
 * @param {Object} options.state - state to be sent to next page
 * @param {string} options.search - query string to be sent to next page
 */

export function navigateStack({
    history,
    pathname = null,
    fullLink = null,
    skipWeChatNewStack = false,
    state = {},
    search = ""
}) {
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        if (fullLink) {
            window.open(fullLink, "_blank", "noopener,noreferrer");
            return
        }
        if (pathname) {
            history.push({ pathname, search, state });
        }
        return
    }
    if (!window.wx) {
        return
    }
    if (skipWeChatNewStack) {
        history.push({ pathname, search, state });
        return
    }
    const webUrl = fullLink ? fullLink : (WEB_DOMAIN + pathname + search)
    window.wx.miniProgram.navigateTo({ url: `/pages/back/back?webUrl=${encodeURIComponent(webUrl)}` })
}

/**
 * Navigate to a specified page and replace the current history entry
 * 
 * @param {Object} options - Navigation options
 * @param {string|null} options.pathname - Target page path, if null no change to current path
 * @param {Object} options.history - Router history object for page navigation
 */
export function navigateReplacePage({ pathname = null, history }) {
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        history.replace(pathname);
        return
    }
    if (!window.wx) {
        return
    }
    const webUrl = WEB_DOMAIN + pathname
    window.wx.miniProgram.redirectTo({ url: `/pages/back/back?webUrl=${encodeURIComponent(webUrl)}` })
}

/**
 * Redirect after login
 * @param {Object} options - Navigation options
 * @param {string|null} options.pathname - Target page path
 * @param {Object} options.history - Router history object
 * @param {Object} options.params - Parameters to pass during navigation
 */
export function loginRedirect({ pathname = null, history, params = {} }) {
    const needWeChatNewStack = !mobileDetectIsInMainNav(pathname)
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        history.replace(pathname, params);
        return
    }
    if (!window.wx) {
        return
    }
    const webUrl = WEB_DOMAIN + pathname
    if (needWeChatNewStack) {
        history.replace('/home');
        window.wx.ready(function () {
            window.wx.miniProgram.navigateTo({ url: `/pages/back/back?webUrl=${encodeURIComponent(webUrl)}` })
        })
        return
    }
    history.replace(pathname);
}

/**
 * Navigate back to the home page, first page in stack
 * 
 * @param {Object} options - Navigation options
 * @param {Object} options.history - Router history object for page navigation
 */
export function navigateBackHomePage({ history }) {
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        history.replace('/home');
        return
    }
    if (!window.wx) {
        return
    }
    window.wx.miniProgram.navigateBack({ delta: 99 })
}

/**
 * Navigate to previous page in stack
 * @param {Object} options - Navigation options
 * @param {Object} options.history - Router history object for page navigation
 * @returns {void}
 */
export function navigateBack({ history }) {
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        history.goBack()
        return
    }
    if (!window.wx) {
        return
    }
    window.wx.miniProgram.navigateBack({ delta: 1 })
}

/**
 * Navigate to root path with deep link, usually using in inbox deep link
 * @param {Object} options - Navigation options
 * @param {string} options.pathname - Target page path
 * @param {Object} options.history - Router history object
 */
export function deepLinkRootPath({ pathname, history }) {
    const isInMiniProgram = inMiniProgram()
    if (!isInMiniProgram) {
        history.push(pathname || '/')
        return
    }
    if (!window.wx) {
        return
    }
    const weChatPath = '/pages/index/index'
    const url = pathname ? `${weChatPath}?scene=${pathname}` : weChatPath
    window.wx.miniProgram.reLaunch({
        url
    })
}

/**
 * Update weChat route title based on current path
 * @param {Object} options - Update options
 * @param {string} options.pathname - Current path name
 */
export function updateRouteTitle({ pathname }) {
    const allRoutes = [...BASIC_WEBAPP_ROUTES, ...ABOUT_NAV_ROUTE, ...USER_NAV_ROUTE, { ...OFFER_ROUTE, path: OFFER_ROUTE.basicPath }]
    const route = allRoutes.find(route => {
        const match = matchPath(pathname, { path: route.path, exact: route.exact });
        return match && route.weChatTitle

    })
    if (route) {
        const pageTitle = getCamelCase({ name: route.weChatTitle })
        window.document.title = `${pageTitle} | ${APP_NAME}`
    }
}

/**
 * Share content to WeChat Mini Program
 * @param {Object} options - Share configuration options
 * @param {string} options.title - Share title
 * @param {string} options.imgUrl - Image URL for sharing
 * @param {string} options.pathname - Path to the page being shared
 * @returns {void}
 */
export function weChatShare({ title, imgUrl, pathname }) {
    if (!window.wx) {
        return
    }
    const encodedLink = encodeURIComponent(pathname)
    const encodedImgUrl = encodeURIComponent(imgUrl)
    const encodedTitle = encodeURIComponent(title)
    const url = `/pages/share/share?imgUrl=${encodedImgUrl}&title=${encodedTitle}&link=${encodedLink}`
    window.wx.miniProgram.navigateTo({ url })
}